"use client";

import { useRef, useState } from "react";
import TopBar from "@/components/TopBar";
import Logo from "@/components/Logo";

type Side = "heads" | "tails";
type Phase = "setup" | "insert" | "flip" | "result";
type FlipResult = { result: Side; won: boolean };

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

export default function FlipPage() {
  const [choice, setChoice] = useState<Side | null>(null);
  const [phase, setPhase] = useState<Phase>("setup");
  const [result, setResult] = useState<FlipResult | null>(null);

  const coinRef = useRef<HTMLDivElement>(null);
  const tossRef = useRef<HTMLDivElement>(null);
  const degRef = useRef(0);
  const running = useRef(false);

  function landingDelta(target: Side) {
    const spins = 6;
    const targetMod = target === "heads" ? 0 : 180;
    const cur = ((degRef.current % 360) + 360) % 360;
    return spins * 360 + ((targetMod - cur + 360) % 360);
  }

  async function play() {
    if (!choice || running.current) return;
    running.current = true;
    setResult(null);

    const apiPromise: Promise<FlipResult> = fetch("/api/flip", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ choice }),
    }).then((r) => r.json());

    setPhase("insert");
    await sleep(950);

    setPhase("flip");
    await sleep(450);

    const data = await apiPromise.catch(() => null);
    const landOn: Side = data?.result ?? "heads";
    const start = degRef.current;
    const end = start + landingDelta(landOn);
    degRef.current = end;

    coinRef.current?.animate(
      [{ transform: `rotateY(${start}deg)` }, { transform: `rotateY(${end}deg)` }],
      { duration: 2200, easing: "cubic-bezier(0.33,0,0.25,1)", fill: "forwards" },
    );
    tossRef.current?.animate(
      [
        { transform: "translateY(0) scale(1)" },
        { transform: "translateY(-20vh) scale(1.12)", offset: 0.5 },
        { transform: "translateY(0) scale(1)" },
      ],
      { duration: 2200, easing: "cubic-bezier(0.33,0,0.25,1)", fill: "forwards" },
    );

    await sleep(2250);
    setResult(data ?? { result: landOn, won: landOn === choice });
    setPhase("result");
    running.current = false;
  }

  function reset() {
    setPhase("setup");
    setResult(null);
  }

  const won = result ? result.result === choice : false;

  return (
    <main className="arena-bg relative flex-1 flex flex-col overflow-hidden">
      <TopBar title="COIN FLIP" />

      {/* ---- SETUP: the arcade cabinet ---- */}
      <div className="flex-1 flex items-center justify-center px-4 py-6">
        <div
          className={
            phase === "insert"
              ? "animate-cabinet-zoom"
              : phase !== "setup"
                ? "opacity-0 pointer-events-none"
                : ""
          }
        >
          <Cabinet
            choice={choice}
            setChoice={setChoice}
            onInsert={play}
            inserting={phase === "insert"}
          />
        </div>
      </div>

      {/* ---- DIVE OVERLAY: inside the machine ---- */}
      {(phase === "flip" || phase === "result") && (
        <div className="fixed inset-0 z-40 arena-bg scanlines flex flex-col animate-dive-in">
          {/* TOP — status */}
          <div className="h-[26vh] flex flex-col items-center justify-end pb-2 text-center px-4">
            {phase === "flip" ? (
              <p className="font-display tracking-[0.4em] text-white/55 text-sm">
                YOU PICKED{" "}
                <span
                  className={
                    choice === "heads" ? "text-sun glow-sun" : "text-electric glow-text"
                  }
                >
                  {choice?.toUpperCase()}
                </span>
              </p>
            ) : (
              result && (
                <>
                  <p className="font-display tracking-[0.4em] text-white/55 text-sm mb-1">
                    IT&apos;S {result.result.toUpperCase()}
                  </p>
                  <h2
                    className={`headline text-5xl sm:text-6xl animate-slam ${
                      won ? "text-sun glow-sun" : "text-energy glow-red"
                    }`}
                  >
                    {won ? "CORRECT!" : "NOT THIS TIME"}
                  </h2>
                </>
              )
            )}
          </div>

          {/* MIDDLE — coin, always centered */}
          <div className="flex-1 flex items-center justify-center coin-stage relative">
            {phase === "result" && won && (
              <div className="pointer-events-none absolute w-[42vw] max-w-[420px] aspect-square rounded-full bg-sun/25 blur-3xl animate-burst" />
            )}
            <div ref={tossRef}>
              <div
                ref={coinRef}
                className="coin"
                style={{ width: "min(56vw, 300px)", height: "min(56vw, 300px)" }}
              >
                <div className="coin-face">
                  <img src="/heads.png?v=3" alt="Heads — Red Bull M.E.O." />
                </div>
                <div className="coin-face coin-back">
                  <img src="/tails.png?v=3" alt="Tails — whistle" />
                </div>
              </div>
            </div>
          </div>

          {/* BOTTOM — play again */}
          <div className="h-[26vh] flex flex-col items-center justify-start pt-4 px-4">
            {phase === "result" && (
              <button onClick={reset} className="btn btn-blue text-lg">
                ▶ PLAY AGAIN
              </button>
            )}
          </div>
        </div>
      )}
    </main>
  );
}

/* ------------------------------------------------------------------ */

function Cabinet({
  choice,
  setChoice,
  onInsert,
  inserting,
}: {
  choice: Side | null;
  setChoice: (s: Side) => void;
  onInsert: () => void;
  inserting: boolean;
}) {
  return (
    <div className="relative w-[min(94vw,460px)] select-none">
      {/* neon side rails */}
      <div className="neon-rail absolute -left-1.5 top-10 bottom-6 w-1.5 rounded-full" />
      <div className="neon-rail absolute -right-1.5 top-10 bottom-6 w-1.5 rounded-full" />

      {/* Marquee */}
      <div className="marquee relative rounded-t-[28px] px-5 pt-3 pb-3 flex items-center justify-center">
        <div className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-8 rounded-md speaker bg-black/40 border border-white/10" />
        <div className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-8 rounded-md speaker bg-black/40 border border-white/10" />
        <Logo height={58} />
      </div>

      {/* Cabinet body */}
      <div className="cabinet rounded-b-[28px] px-5 pt-5 pb-4">
        {/* CRT screen */}
        <div className="crt relative scanlines p-5">
          <p className="text-center font-display tracking-[0.35em] text-electric/90 text-sm mb-4 glow-text">
            CHOOSE YOUR SIDE
          </p>

          <div className="grid grid-cols-2 gap-3">
            <SideButton
              side="heads"
              img="/heads.png?v=3"
              active={choice === "heads"}
              onClick={() => setChoice("heads")}
            />
            <SideButton
              side="tails"
              img="/tails.png?v=3"
              active={choice === "tails"}
              onClick={() => setChoice("tails")}
            />
          </div>

          <div className="mt-4 text-center text-xs h-4 font-display tracking-[0.3em] text-white/35">
            ● SYSTEM ONLINE // READY P1
          </div>
        </div>

        {/* Control deck */}
        <div className="mt-5 flex items-end justify-between gap-3 px-1">
          <Joystick />
          <CoinSlot inserting={inserting} />
          <div className="flex flex-col items-center gap-2">
            <div className="flex gap-2">
              <span className="arcade-btn w-7 h-7 bg-energy" />
              <span className="arcade-btn w-7 h-7 bg-electric" />
            </div>
            <div className="flex gap-2">
              <span className="arcade-btn w-7 h-7 bg-sun" />
              <span className="arcade-btn w-7 h-7 bg-white/85" />
            </div>
          </div>
        </div>

        <button
          disabled={!choice || inserting}
          onClick={onInsert}
          className="btn btn-red w-full mt-5 text-lg"
        >
          {inserting ? "··· INSERTING ···" : "▶ INSERT COIN"}
        </button>
        <div className="h-4 mt-1.5 text-center">
          {!choice && (
            <p className="text-white/35 text-xs animate-blink font-display tracking-widest">
              PICK HEADS OR TAILS TO START
            </p>
          )}
        </div>
      </div>

      <div className="mx-6 h-4 bg-gradient-to-b from-metal to-black rounded-b-lg" />
    </div>
  );
}

function SideButton({
  side,
  img,
  active,
  onClick,
}: {
  side: Side;
  img: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={`relative rounded-xl p-3 flex flex-col items-center gap-2 border-2 transition-all ${
        active
          ? "border-sun bg-sun/10 shadow-[0_0_28px_-4px_rgba(255,209,0,0.65)] -translate-y-1"
          : "border-white/10 bg-black/30 hover:border-electric/60 hover:-translate-y-0.5"
      }`}
    >
      <img
        src={img}
        alt={side}
        className="w-20 h-20 object-contain drop-shadow-[0_4px_10px_rgba(0,0,0,0.6)]"
      />
      <span
        className={`font-display tracking-[0.2em] text-sm ${
          active ? "text-sun glow-sun" : "text-white/70"
        }`}
      >
        {side.toUpperCase()}
      </span>
    </button>
  );
}

function CoinSlot({ inserting }: { inserting: boolean }) {
  return (
    <div className="relative flex flex-col items-center">
      {inserting && (
        <img
          src="/heads.png?v=3"
          alt=""
          className="absolute -top-3 left-1/2 -translate-x-1/2 w-9 h-9 object-contain animate-coin-drop z-10"
        />
      )}
      <div className="w-16 h-24 rounded-lg bg-gradient-to-b from-[#1a1d22] to-black border border-electric/30 flex flex-col items-center justify-start pt-2.5 shadow-[inset_0_2px_8px_rgba(0,0,0,0.8)]">
        <div className="w-9 h-2 rounded-full bg-black border-2 border-sun/60 shadow-[0_0_8px_rgba(255,209,0,0.4)]" />
        <div className="mt-2 font-display text-[9px] tracking-widest text-sun/80 text-center leading-tight glow-sun">
          INSERT
          <br />
          COIN
        </div>
      </div>
    </div>
  );
}

function Joystick() {
  return (
    <div className="flex flex-col items-center">
      <div className="w-2.5 h-7 bg-gradient-to-b from-metal to-black rounded-full" />
      <div className="w-8 h-8 -mt-1 rounded-full bg-gradient-to-b from-energy to-[#8a0410] shadow-[0_0_16px_-2px_rgba(227,6,19,0.9),inset_0_3px_6px_rgba(255,255,255,0.4)]" />
      <div className="w-11 h-2.5 rounded-full bg-black mt-1" />
    </div>
  );
}
